import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Percent,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  FileBarChart
} from "lucide-react";

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatPercent(value) {
  return `${value.toFixed(1)}%`;
}

export default function DREGeralPage() {
  const [loading, setLoading] = useState(true);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDRE();
  }, [ano, mes]);

  const fetchDRE = async () => {
    setLoading(true);
    try {
      let url = `${API}/financeiro/dre-geral?ano=${ano}`;
      if (mes) url += `&mes=${mes}`;
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar DRE");
    } finally {
      setLoading(false);
    }
  };

  const anos = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const meses = [
    { value: "1", label: "Janeiro" },
    { value: "2", label: "Fevereiro" },
    { value: "3", label: "Março" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Maio" },
    { value: "6", label: "Junho" },
    { value: "7", label: "Julho" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Setembro" },
    { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" },
    { value: "12", label: "Dezembro" }
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="dre-geral-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">DRE Geral</h1>
            <p className="text-slate-500">Demonstração do Resultado do Exercício - LuvCorp</p>
          </div>
          <div className="flex gap-2">
            <NativeSelect
              value={ano.toString()}
              onValueChange={(v) => setAno(parseInt(v))}
              options={anos.map(a => ({ value: a.toString(), label: a.toString() }))}
              className="w-[120px]"
            />
            <NativeSelect
              value={mes?.toString() || "all"}
              onValueChange={(v) => setMes(v === "all" ? null : parseInt(v))}
              placeholder="Ano Completo"
              options={[
                { value: "all", label: "Ano Completo" },
                ...meses.map(m => ({ value: m.value, label: m.label }))
              ]}
              className="w-[140px]"
            />
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Receita Bruta</p>
                  <p className="text-2xl font-bold text-luvcorp-navy">{formatCurrency(data?.receita_bruta || 0)}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Custos e Despesas</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency((data?.custos_servicos || 0) + (data?.despesas_operacionais || 0))}</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Lucro Operacional</p>
                  <p className={`text-2xl font-bold ${(data?.lucro_operacional || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(data?.lucro_operacional || 0)}
                  </p>
                </div>
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${(data?.lucro_operacional || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <DollarSign className={`h-6 w-6 ${(data?.lucro_operacional || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Margem Operacional</p>
                  <p className={`text-2xl font-bold ${(data?.margem_operacional || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatPercent(data?.margem_operacional || 0)}
                  </p>
                </div>
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${(data?.margem_operacional || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Percent className={`h-6 w-6 ${(data?.margem_operacional || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* DRE Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileBarChart className="h-5 w-5 text-luvcorp-gold" />
                Demonstração do Resultado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="font-semibold text-luvcorp-navy">RECEITA BRUTA</span>
                  <span className="font-semibold text-green-600">{formatCurrency(data?.receita_bruta || 0)}</span>
                </div>
                <div className="flex justify-between py-2 pl-4 text-slate-600">
                  <span>(-) Deduções</span>
                  <span className="text-red-500">({formatCurrency(data?.deducoes || 0)})</span>
                </div>
                <div className="flex justify-between py-2 border-b bg-slate-50 px-2 rounded">
                  <span className="font-semibold text-luvcorp-navy">(=) RECEITA LÍQUIDA</span>
                  <span className="font-semibold">{formatCurrency(data?.receita_liquida || 0)}</span>
                </div>
                <div className="flex justify-between py-2 pl-4 text-slate-600">
                  <span>(-) Custos dos Serviços</span>
                  <span className="text-red-500">({formatCurrency(data?.custos_servicos || 0)})</span>
                </div>
                <div className="flex justify-between py-2 border-b bg-slate-50 px-2 rounded">
                  <span className="font-semibold text-luvcorp-navy">(=) LUCRO BRUTO</span>
                  <span className={`font-semibold ${(data?.lucro_bruto || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(data?.lucro_bruto || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 pl-4 text-slate-600">
                  <span>(-) Despesas Operacionais</span>
                  <span className="text-red-500">({formatCurrency(data?.despesas_operacionais || 0)})</span>
                </div>
                <div className="flex justify-between py-3 bg-luvcorp-navy text-white px-3 rounded-lg mt-2">
                  <span className="font-bold">(=) LUCRO OPERACIONAL</span>
                  <span className="font-bold">{formatCurrency(data?.lucro_operacional || 0)}</span>
                </div>
              </div>

              <div className="flex gap-4 mt-6 pt-4 border-t">
                <div className="flex-1 text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-slate-500">Margem Bruta</p>
                  <p className="text-lg font-bold text-blue-700">{formatPercent(data?.margem_bruta || 0)}</p>
                </div>
                <div className="flex-1 text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-slate-500">Margem Operacional</p>
                  <p className="text-lg font-bold text-green-700">{formatPercent(data?.margem_operacional || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Evolution Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-luvcorp-gold" />
                Evolução Mensal
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.evolucao_mensal?.length > 0 ? (
                <div className="space-y-3">
                  {data.evolucao_mensal.map((item, index) => {
                    const maxValue = Math.max(...data.evolucao_mensal.map(i => Math.max(i.receitas, i.despesas)));
                    const receitaWidth = maxValue > 0 ? (item.receitas / maxValue) * 100 : 0;
                    const despesaWidth = maxValue > 0 ? (item.despesas / maxValue) * 100 : 0;
                    
                    return (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{item.mes}</span>
                          <span className={item.resultado >= 0 ? "text-green-600" : "text-red-600"}>
                            {formatCurrency(item.resultado)}
                          </span>
                        </div>
                        <div className="flex gap-1 h-6">
                          <div 
                            className="bg-green-400 rounded-l transition-all"
                            style={{ width: `${receitaWidth}%` }}
                            title={`Receitas: ${formatCurrency(item.receitas)}`}
                          />
                          <div 
                            className="bg-red-400 rounded-r transition-all"
                            style={{ width: `${despesaWidth}%` }}
                            title={`Despesas: ${formatCurrency(item.despesas)}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex gap-4 pt-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-400 rounded"></div>
                      <span className="text-slate-500">Receitas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-400 rounded"></div>
                      <span className="text-slate-500">Despesas</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <BarChart3 className="h-16 w-16 mb-4" />
                  <p>Nenhum lançamento no período</p>
                  <p className="text-sm mt-2">Cadastre lançamentos financeiros para visualizar o gráfico</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="border-0 shadow-sm bg-blue-50 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <FileBarChart className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Total de lançamentos no período: {data?.total_lancamentos || 0}</p>
                <p className="text-sm text-blue-700 mt-1">
                  {data?.total_lancamentos === 0 
                    ? "Cadastre lançamentos em Financeiro → Centro de Custo ou através da API para popular este relatório."
                    : `Dados consolidados de ${mes ? meses.find(m => m.value === mes.toString())?.label : 'todo o ano'} de ${ano}.`
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
